import { EditOutlined, FileTextOutlined, FormOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useRequest } from 'ahooks';
import ConversationTopBar from 'components/chats/ConversationTopBar';
import PatientDocumentsList from 'components/lists/PatientDocumentsList';
import PatientProfileModal from 'components/modals/PatientProfileModal';
import QuestionnaireResponseViewer from 'components/questionnaire/QuestionnaireResponseViewer';
import { Button } from 'components/ui/button';
import { Form, FormControl, FormField, FormItem } from 'components/ui/form';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'components/ui/sheet';
import { Spinner } from 'components/ui/spinner';
import { Textarea } from 'components/ui/textarea';
import useSelectConversations from 'hooks/useSelectConversations';
import posthog from 'posthog-js';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import ApiDoctorConversationsManager from 'services/api/ApiDoctorConversationsManager';
import ApiPatientDocumentsManager from 'services/api/ApiPatientDocumentsManager';
import ApiQuestionnaireManager from 'services/api/ApiQuestionnaireManager';
import ApiSymptomTrackingManager from 'services/api/ApiSymptomTrackingManager';
import { ConversationWithMessagesDTO } from 'services/api/models/ConversationDTO';
import { QuestionnaireResponseDTO } from 'services/api/models/QuestionnaireDTO';
import { toast } from 'sonner';
import { conversationActions } from 'store/conversations';

type SummaryFormValues = { summary: string };

export default function CaregiverDossierPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { conversationId } = useParams<{ conversationId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { conversationMetadata, conversationSummaryDraft } = useSelectConversations();

  const form = useForm<SummaryFormValues>({ defaultValues: { summary: conversationSummaryDraft || '' } });
  const [displayedSummary, setDisplayedSummary] = useState<string>('');
  const summaryValue = form.watch('summary');

  // Questionnaire state
  const [openOnboardingModal, setOpenOnboardingModal] = useState(false);
  const [isMedicalDrawerOpen, setIsMedicalDrawerOpen] = useState(false);
  const [medicalFetchEnabled, setMedicalFetchEnabled] = useState(false);

  // we need this ref to access the summary value on unmount
  const summaryValueRef = useRef(conversationSummaryDraft || '');
  useEffect(() => {
    if (summaryValue !== undefined) summaryValueRef.current = summaryValue;
  }, [summaryValue]);

  // The conversations cache only holds the caregiver's own conversations, so it misses
  // e.g. another caregiver's conversation opened from the patient's history. Fall back to
  // the conversation fetched below so the dossier always resolves the right patient.
  const [loadedConversation, setLoadedConversation] = useState<ConversationWithMessagesDTO>();
  const conversation =
    conversationMetadata?.conversation ?? (loadedConversation?.id === conversationId ? loadedConversation : undefined);
  const patient = conversation?.patient;
  const questionnaireResponseId = conversation?.questionnaire_response_id;

  // Check if onboarding questionnaire exists
  const { data: onboardingSurveys, loading: loadingOnboarding } = useRequest(
    async () => {
      if (!patient?.id) return { hasOnboarding: false };
      const [v1, v2] = await Promise.allSettled([
        ApiSymptomTrackingManager.getOnboardingSurvey(patient.id),
        ApiSymptomTrackingManager.getOnboardingSurveyV2(patient.id),
      ]);
      return { hasOnboarding: v1.status === 'fulfilled' || v2.status === 'fulfilled' };
    },
    { refreshDeps: [patient?.id] },
  );

  const { data: medicalQuestionnaire, isFetching: loadingMedical } = useQuery<QuestionnaireResponseDTO | null>({
    queryKey: ['questionnaire', 'responseForPatient', questionnaireResponseId],
    queryFn: () =>
      questionnaireResponseId
        ? ApiQuestionnaireManager.getResponseForPatient(questionnaireResponseId).catch(() => null)
        : Promise.resolve(null),
    staleTime: 1000 * 60 * 5,
    enabled: medicalFetchEnabled && !!questionnaireResponseId,
  });
  // Use the schema that was embedded in the response — this is the version the
  // patient actually answered, not the current active schema.
  const medicalSchema = medicalQuestionnaire?.schema ?? null;

  function openMedicalDrawer() {
    setMedicalFetchEnabled(true);
    setIsMedicalDrawerOpen(true);
    if (searchParams.has('openMedical')) {
      setSearchParams(
        params => {
          params.delete('openMedical');
          return params;
        },
        { replace: true },
      );
    }
  }

  // Auto-open medical questionnaire when ?openMedical=1 is in the URL
  useEffect(() => {
    if (searchParams.get('openMedical') === '1' && questionnaireResponseId) {
      openMedicalDrawer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireResponseId]);

  const {
    data: documents,
    loading: loadingDocuments,
    refresh: refreshDocuments,
  } = useRequest(() => ApiPatientDocumentsManager.listForCaregiver({ patientId: patient?.id }), {
    // Without a patient id the endpoint returns the documents of every patient shared
    // with this caregiver, so wait until the patient is known.
    ready: !!patient?.id,
    refreshDeps: [patient?.id],
    onError: () => {
      toast.error(t('documents.errors.loadError'));
    },
  });

  const { loading: loadingSummary, run: loadSummary } = useRequest(
    () => ApiDoctorConversationsManager.getDoctorConversationMessageList({ conversationId: conversationId! }),
    {
      onSuccess: result => {
        setLoadedConversation(result);
        const summary = result.summary || '';
        setDisplayedSummary(summary);
        // Use draft if available, otherwise use the loaded summary
        const initialValue = conversationSummaryDraft || summary;
        form.setValue('summary', initialValue);
        summaryValueRef.current = initialValue;
      },
      onError: () => {
        toast.error(t('chats.summary.summaryLoadError'));
      },
    },
  );

  function saveDraft() {
    if (conversationId) {
      dispatch(conversationActions.setConversationSummaryDraft({ conversationId, summary: summaryValueRef.current }));
    }
  }

  // Load summary and documents on mount
  React.useEffect(() => {
    if (conversationId) {
      loadSummary();
    }
    return () => {
      // Save draft when component unmounts
      saveDraft();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, loadSummary]);

  // Update form when draft changes (e.g., when navigating between conversations)
  useEffect(() => {
    if (conversationSummaryDraft !== undefined) {
      const initialValue = conversationSummaryDraft || displayedSummary;
      form.setValue('summary', initialValue);
      summaryValueRef.current = initialValue;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, conversationSummaryDraft]);

  const { loading: savingSummary, run: saveSummary } = useRequest(
    (summary: string) => ApiDoctorConversationsManager.updateConversationSummary(conversationId!, summary),
    {
      manual: true,
      onSuccess: (_, [summary]) => {
        setDisplayedSummary(summary || '');
        summaryValueRef.current = summary || '';
        // Clear draft when summary is successfully saved
        if (conversationId) {
          dispatch(conversationActions.setConversationSummaryDraft({ conversationId, summary: '' }));
        }
        toast.success(t('chats.summary.summarySaved'));
      },
      onError: () => {
        toast.error(t('chats.summary.summarySaveError'));
      },
    },
  );

  const onSummarySubmit = (values: SummaryFormValues) => {
    saveSummary(values.summary);
    posthog.capture('patient_file.summary_updated');
  };

  const sectionTitleClassName = 'mt-6 flex items-center gap-2 text-lg font-semibold';
  const sectionClassName = 'flex w-full max-w-content flex-col rounded-md';
  const questionnaireRowClassName = 'flex items-center justify-between border-b border-border py-2.5 last:border-b-0';
  const questionnairePlaceholderClassName = 'text-sm italic text-subtle-foreground';
  const emptyStateClassName = 'p-5 text-center text-muted-foreground';

  return (
    <div className="flex h-full flex-col bg-background">
      <ConversationTopBar
        showBackButton
        avatar={patient?.photo_url}
        name={patient?.display_name}
        patientId={patient?.id}
        onBackButtonClick={() => {
          saveDraft();
          navigate(-1);
        }}
      />

      {openOnboardingModal && patient?.id && patient?.display_name && (
        <PatientProfileModal
          patientId={patient.id}
          patientName={patient.display_name}
          open
          onClose={() => setOpenOnboardingModal(false)}
        />
      )}

      <Sheet open={isMedicalDrawerOpen} onOpenChange={setIsMedicalDrawerOpen}>
        {/* a bottom sheet that leaves the conversation visible above it */}
        <SheetContent side="bottom" className="h-[85dvh] overflow-y-auto" aria-describedby={undefined}>
          <SheetHeader>
            <SheetTitle data-testid="dossier-sheet-title">
              {t('medicalProfile.caregiver.questionnaireModalTitle')}
            </SheetTitle>
          </SheetHeader>
          <div className="px-4 pb-4">
            {loadingMedical ? (
              <div className="flex justify-center p-10">
                <Spinner className="size-6" />
              </div>
            ) : medicalQuestionnaire && medicalSchema ? (
              <QuestionnaireResponseViewer
                response={medicalQuestionnaire}
                schema={medicalSchema}
                responseId={medicalQuestionnaire.id}
              />
            ) : (
              <p>{t('medicalProfile.caregiver.noResponse')}</p>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex flex-col items-center overflow-scroll p-4">
        {/* Questionnaire Section */}
        <div className={`${sectionClassName} mb-2`}>
          <div className={sectionTitleClassName}>
            <FormOutlined />
            {t('chats.dossier.questionnaires')}
          </div>
          <div className="mt-2">
            <div className={questionnaireRowClassName}>
              <span className="font-medium">{t('chats.dossier.onboardingQuestionnaire')}</span>
              {loadingOnboarding ? (
                <Spinner className="size-4" />
              ) : onboardingSurveys?.hasOnboarding ? (
                <Button variant="outline" onClick={() => setOpenOnboardingModal(true)}>
                  {t('chats.dossier.viewQuestionnaire')}
                </Button>
              ) : (
                <span className={questionnairePlaceholderClassName}>
                  {t('chats.dossier.noOnboardingQuestionnaire')}
                </span>
              )}
            </div>
            <div className={questionnaireRowClassName}>
              <span className="font-medium">{t('chats.dossier.medicalQuestionnaire')}</span>
              {questionnaireResponseId ? (
                <Button variant="outline" loading={loadingMedical} onClick={openMedicalDrawer}>
                  {t('chats.dossier.viewQuestionnaire')}
                </Button>
              ) : (
                <span className={questionnairePlaceholderClassName}>{t('chats.dossier.noMedicalQuestionnaire')}</span>
              )}
            </div>
          </div>
        </div>

        {/* Notes Section */}
        <div className={sectionClassName}>
          <div className={sectionTitleClassName}>
            <EditOutlined />
            {t('chats.summary.title')}
          </div>
          <p className="mt-1 ml-1 text-sm font-normal text-subtle-foreground">{t('chats.summary.subtitle')}</p>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSummarySubmit)} className="flex min-h-0 flex-1 flex-col gap-4">
              <FormField
                control={form.control}
                name="summary"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder={t('chats.summary.summaryPlaceholder')}
                        rows={4}
                        className="w-full"
                        disabled={loadingSummary || savingSummary}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="submit"
                  size="lg"
                  loading={savingSummary}
                  disabled={!summaryValue || summaryValue === displayedSummary}
                >
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </Form>
        </div>

        {/* Documents Section */}
        <div className={sectionClassName}>
          <div className={sectionTitleClassName}>
            <FileTextOutlined />
            {t('chats.dossier.documents')}
          </div>
          <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
            {(loadingDocuments || !patient?.id) && documents === undefined ? (
              <div className={emptyStateClassName}>{t('common.loading')}</div>
            ) : !documents || documents.length === 0 ? (
              <div className={emptyStateClassName}>{t('chats.dossier.noDocuments')}</div>
            ) : (
              <PatientDocumentsList documents={documents} isCaregiver={true} onRefresh={refreshDocuments} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
